import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Allega il token JWT (Bearer) a ogni richiesta.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Su 401 (sessione scaduta/assente) pulisce la sessione e notifica l'app (senza reload forzato).
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const url = error?.config?.url || "";
    if (error?.response?.status === 401 && !url.includes("/auth/login") && !url.includes("/auth/me")) {
      clearAuthSession();
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    return Promise.reject(error);
  }
);

export const getAuthUser = () => {
  try { return JSON.parse(localStorage.getItem("auth_user") || "null"); } catch { return null; }
};
export const setAuthSession = (token, user) => {
  localStorage.setItem("access_token", token);
  localStorage.setItem("auth_user", JSON.stringify(user));
};
export const clearAuthSession = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("auth_user");
};
// L'operatore che registra le attività = utente loggato.
export const getOperator = () => getAuthUser()?.name || "Operatore";

export const AuthApi = {
  login: (username, password) => api.post("/auth/login", { username, password }).then((r) => r.data),
  devLogin: () => api.post("/auth/dev-login").then((r) => r.data),
  sso: (token) => api.post("/auth/sso", { token }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data).catch(() => {}),
  listUsers: () => api.get("/auth/users").then((r) => r.data),
  createUser: (body) => api.post("/auth/users", body).then((r) => r.data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`).then((r) => r.data),
  resetPassword: (id, password) => api.patch(`/auth/users/${id}/password`, { password }).then((r) => r.data),
  changePassword: (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  loginEvents: (limit = 100) => api.get("/auth/login-events", { params: { limit } }).then((r) => r.data),
};

export const StatusApi = {
  listComuni: () => api.get("/comuni").then((r) => r.data),
  addComune: (name) => api.post("/comuni", { name }).then((r) => r.data),
  deleteComune: (id) => api.delete(`/comuni/${id}`).then((r) => r.data),
  seedComuni: () => api.post("/comuni/seed").then((r) => r.data),
  comuniCounts: () => api.get("/comuni/counts").then((r) => r.data),
  scrapeHistory: () => api.get("/comuni/scrape-history").then((r) => r.data),
  runBackup: () => api.post("/backup/run").then((r) => r.data),
  getSettings: () => api.get("/settings").then((r) => r.data),
  saveApiKey: (google_api_key) => api.put("/settings", { google_api_key }).then((r) => r.data),
  saveGeminiKey: (gemini_api_key) => api.put("/settings/gemini", { gemini_api_key }).then((r) => r.data),
  startAiEnrich: (comune, limit = 40) => api.post("/ai-enrich", { comune, limit }).then((r) => r.data),
  aiEnrichLead: (id) => api.post(`/leads/${id}/ai-enrich`).then((r) => r.data),
  listLeads: (params) => api.get("/leads", { params }).then((r) => r.data),
  updateLead: (id, data) => api.patch(`/leads/${id}`, data).then((r) => r.data),
  enrichLead: (id) => api.post(`/leads/${id}/enrich`).then((r) => r.data),
  startEnrich: (comune) => api.post("/enrich", { comune }).then((r) => r.data),
  importTelemaco: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/import/telemaco", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteLead: (id) => api.delete(`/leads/${id}`).then((r) => r.data),
  deleteLeadsByComune: (comune) => api.delete("/leads", { params: { comune } }).then((r) => r.data),
  dedupeLeads: () => api.post("/leads/dedupe").then((r) => r.data),
  listCampaigns: () => api.get("/campaigns").then((r) => r.data),
  createCampaign: (body) => api.post("/campaigns", body).then((r) => r.data),
  updateCampaign: (id, body) => api.patch(`/campaigns/${id}`, body).then((r) => r.data),
  deleteCampaign: (id) => api.delete(`/campaigns/${id}`).then((r) => r.data),
  getCampaign: (id) => api.get(`/campaigns/${id}`).then((r) => r.data),
  activitiesFeed: (params) => api.get("/activities/feed", { params }).then((r) => r.data),
  assignActivities: (id, body) => api.post(`/campaigns/${id}/assign`, body).then((r) => r.data),
  unassignActivities: (id, body) => api.post(`/campaigns/${id}/unassign`, body).then((r) => r.data),
  addCampaignMembers: (id, lead_ids) => api.post(`/campaigns/${id}/members`, { lead_ids }).then((r) => r.data),
  removeCampaignMembers: (id, lead_ids) => api.delete(`/campaigns/${id}/members`, { data: { lead_ids } }).then((r) => r.data),
  campaignCandidates: (id, params) => api.get(`/campaigns/${id}/candidates`, { params }).then((r) => r.data),
  sendCampaignById: (id, body) => api.post(`/campaigns/${id}/send`, { user_name: getOperator(), ...body }).then((r) => r.data),
  listPromos: () => api.get("/promos").then((r) => r.data),
  createPromo: (promo) => api.post("/promos", promo).then((r) => r.data),
  deletePromo: (id) => api.delete(`/promos/${id}`).then((r) => r.data),
  previewProperty: (url) => api.post("/property/preview", { url }).then((r) => r.data),
  getPromo: (id) => api.get(`/promos/${id}`).then((r) => r.data),
  sendCampaign: (body) => api.post("/campaign/send", { user_name: getOperator(), ...body }).then((r) => r.data),
  testSendCampaign: (body) => api.post("/campaign/test-send", body).then((r) => r.data),
  previewCampaign: (body) => api.post("/campaign/preview", body).then((r) => r.data),
  startScrape: (body) => api.post("/scrape", body).then((r) => r.data),
  getJob: (id) => api.get(`/scrape/jobs/${id}`).then((r) => r.data),
  stats: () => api.get("/stats").then((r) => r.data),
  submitFunnelContact: (data) => api.post("/funnel/contact", data).then((r) => r.data),
  listFunnelLeads: () => api.get("/funnel/leads").then((r) => r.data),
  updateFunnelLead: (id, data) => api.patch(`/funnel/leads/${id}`, data).then((r) => r.data),
  deleteFunnelLead: (id) => api.delete(`/funnel/leads/${id}`).then((r) => r.data),
  funnelSummary: () => api.get("/funnel/summary").then((r) => r.data),
  trackVisit: (token) => api.post("/track/visit", null, { params: { token } }).then((r) => r.data).catch(() => {}),
  trackClick: (token, url) => api.post("/track/landing-click", { token, url }).then((r) => r.data).catch(() => {}),
  logCall: (lead_id, outcome = "", status = "") => api.post("/activities/call", { lead_id, outcome, status, user_name: getOperator() }).then((r) => r.data),
  leadActivities: (id) => api.get(`/leads/${id}/activities`).then((r) => r.data),
  weeklyReport: () => api.get("/report/weekly").then((r) => r.data),
  weeklyStats: (weeks = 8) => api.get("/stats/weekly", { params: { weeks } }).then((r) => r.data),
  resetStats: (before) => api.post("/reset-stats", { before }).then((r) => r.data),
  listRecalls: () => api.get("/recalls").then((r) => r.data),
  leadsByStreet: (street, comune) => api.get("/leads/by-street", { params: { street, comune } }).then((r) => r.data),
  globalSearch: (q) => api.get("/search", { params: { q } }).then((r) => r.data),
  deleteActivity: (id) => api.delete(`/activities/${id}`).then((r) => r.data),
  updateActivity: (id, outcome) => api.patch(`/activities/${id}`, { outcome }).then((r) => r.data),
};
